import api from 'services/api';
import { array, enforce, nullable, num, object, runSchema, str, union, withDefault, type UnionBranchShape } from 'validation';

// Source of truth for the wire shape: nap-backend `routes/workspace.js`
// (`GET /workspace/contents`) — see nap-frontend #887 / nap-backend #889. The
// endpoint merges every folder-scoped object type (plus zones as tree roots)
// into one sorted, keyset-paginated list. Each row is Shape B: uniform common
// columns + one per-type `extras` object carrying that type's action gating
// fields. Wire keys are snake_case; this module converts to the camelCase
// `WorkspaceItem` the UI consumes. Validated data is deeply readonly.

// Common columns shared by every row. `zoneId`/`zoneName` are nullable because
// the endpoint derives them via the folder join for the zoneless types
// (connections, python_libraries); `folderId` is null for a container's
// folder-less objects and for zones / root folders.
interface WorkspaceItemCommon {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zoneId: number | null;
  readonly zoneName: string | null;
  readonly folderId: number | null;
}

/**
 * A merged-contents row. Discriminated on `type`; per-type extras are flattened
 * to top-level camelCase fields (the wire nests them under `extras`). Only the
 * four types with gating state carry extra fields — the rest are the common
 * columns alone. `zone` is not folder-scoped: it is returned only for
 * `parent=root` (the tree roots).
 */
export type WorkspaceItem =
  | (WorkspaceItemCommon & { readonly type: 'zone' })
  | (WorkspaceItemCommon & { readonly type: 'folder' })
  | (WorkspaceItemCommon & { readonly type: 'integration' })
  | (WorkspaceItemCommon & { readonly type: 'lock' })
  | (WorkspaceItemCommon & { readonly type: 'trigger' })
  | (WorkspaceItemCommon & { readonly type: 'scheduling_window' })
  | (WorkspaceItemCommon & { readonly type: 'recurrence_definition' })
  | (WorkspaceItemCommon & { readonly type: 'report_definition' })
  | (WorkspaceItemCommon & { readonly type: 'monitoring_dashboard' })
  | (WorkspaceItemCommon & { readonly type: 'python_library' })
  | (WorkspaceItemCommon & { readonly type: 'table' })
  | (WorkspaceItemCommon & { readonly type: 'queue'; readonly status: string })
  | (WorkspaceItemCommon & { readonly type: 'event'; readonly eventType: string })
  | (WorkspaceItemCommon & { readonly type: 'connection'; readonly connectionType: string })
  // `nodeType` is the node definition's type string; the UI derives
  // `isDeprecated` from it against the `/lists/node_types` LOV (a node whose
  // type is no longer registered is deprecated) — the backend emits no
  // deprecation flag.
  | (WorkspaceItemCommon & { readonly type: 'node_definition'; readonly nodeType: string });

export type WorkspaceItemType = WorkspaceItem['type'];

/**
 * The folder-scoped object types shown in the contents pane. Excludes `folder`
 * (folders come from the tree model so the tree fetch is reused and folders
 * render first) and `zone` (a root, never a container's child). The contents
 * thunk requests exactly these so folders aren't fetched twice.
 */
export const WORKSPACE_OBJECT_TYPES: readonly WorkspaceItemType[] = [
  'node_definition',
  'integration',
  'event',
  'lock',
  'trigger',
  'queue',
  'scheduling_window',
  'recurrence_definition',
  'report_definition',
  'monitoring_dashboard',
  'python_library',
  'table',
  'connection',
];

export interface WorkspaceContents {
  readonly items: readonly WorkspaceItem[];
  readonly total: number;
  // Opaque keyset cursor (base64url of the last row's sort key). Pass back
  // verbatim as the `cursor` param to fetch the next page; null on the last page.
  readonly nextCursor: string | null;
}

// The wire row: common snake_case columns + `type` + a per-type `extras` object.
// Only the four stateful types declare `extras`; for the rest the wire's
// `extras: {}` is simply ignored (the `object` parser drops unknown fields).
type WorkspaceItemWire =
  | (WireCommon & { readonly type: 'zone' })
  | (WireCommon & { readonly type: 'folder' })
  | (WireCommon & { readonly type: 'integration' })
  | (WireCommon & { readonly type: 'lock' })
  | (WireCommon & { readonly type: 'trigger' })
  | (WireCommon & { readonly type: 'scheduling_window' })
  | (WireCommon & { readonly type: 'recurrence_definition' })
  | (WireCommon & { readonly type: 'report_definition' })
  | (WireCommon & { readonly type: 'monitoring_dashboard' })
  | (WireCommon & { readonly type: 'python_library' })
  | (WireCommon & { readonly type: 'table' })
  | (WireCommon & { readonly type: 'queue'; readonly extras: { readonly status: string } })
  | (WireCommon & { readonly type: 'event'; readonly extras: { readonly event_type: string } })
  | (WireCommon & { readonly type: 'connection'; readonly extras: { readonly connection_type: string } })
  | (WireCommon & { readonly type: 'node_definition'; readonly extras: { readonly node_type: string } });

interface WireCommon {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_id: number | null;
  readonly zone_name: string | null;
  readonly folder_id: number | null;
}

// The common-column parsers, spread into every branch shape (the discriminator
// `type` is asserted by `union` from the branch key and omitted here).
const commonShape = {
  id: num(),
  name: str(),
  description: nullable(str()),
  zone_id: nullable(num()),
  zone_name: nullable(str()),
  folder_id: nullable(num()),
};

// A branch with no gating state — just the common columns.
const plainBranch = <V extends WorkspaceItemType>(): UnionBranchShape<WorkspaceItemWire, 'type', V> =>
  commonShape as UnionBranchShape<WorkspaceItemWire, 'type', V>;

const workspaceItemWireSchema = union<WorkspaceItemWire, 'type'>('type', {
  zone: plainBranch(),
  folder: plainBranch(),
  integration: plainBranch(),
  lock: plainBranch(),
  trigger: plainBranch(),
  scheduling_window: plainBranch(),
  recurrence_definition: plainBranch(),
  report_definition: plainBranch(),
  monitoring_dashboard: plainBranch(),
  python_library: plainBranch(),
  table: plainBranch(),
  queue: { ...commonShape, extras: withDefault(object<{ status: string }>({ status: str() }), { status: '' }) },
  event: { ...commonShape, extras: withDefault(object<{ event_type: string }>({ event_type: str() }), { event_type: '' }) },
  connection: { ...commonShape, extras: withDefault(object<{ connection_type: string }>({ connection_type: str() }), { connection_type: '' }) },
  node_definition: { ...commonShape, extras: withDefault(object<{ node_type: string }>({ node_type: str() }), { node_type: '' }) },
});

const workspaceContentsSchema = object<{ items: readonly WorkspaceItemWire[]; total: number; next_cursor: string | null }>({
  items: array(workspaceItemWireSchema),
  total: num(),
  next_cursor: nullable(str()),
});

const wireToItem = (wire: WorkspaceItemWire): WorkspaceItem => {
  const common: WorkspaceItemCommon = {
    id: wire.id,
    name: wire.name,
    description: wire.description,
    zoneId: wire.zone_id,
    zoneName: wire.zone_name,
    folderId: wire.folder_id,
  };
  switch (wire.type) {
    case 'queue':
      return { ...common, type: 'queue', status: wire.extras.status };
    case 'event':
      return { ...common, type: 'event', eventType: wire.extras.event_type };
    case 'connection':
      return { ...common, type: 'connection', connectionType: wire.extras.connection_type };
    case 'node_definition':
      return { ...common, type: 'node_definition', nodeType: wire.extras.node_type };
    default:
      return { ...common, type: wire.type };
  }
};

/**
 * Parse a raw `GET /workspace/contents` response. Structural failure throws;
 * a row whose `type` the frontend doesn't know is dropped (the `union` parser
 * can't narrow it), and per-field drift defaults with a logged warning. Kept
 * separate from the fetch so it is unit-testable against fixtures without a
 * live/mock server.
 */
export const parseWorkspaceContents = (raw: unknown, source: string): WorkspaceContents => {
  const result = runSchema(workspaceContentsSchema, raw, source);
  if (!result.ok) {
    throw new Error(`${source}: workspace contents response failed validation`);
  }
  // Drop any row whose identity `id` drifted: `num()` would default it to 0,
  // which collides the React key (`${type}:${id}`) across rows and binds row
  // actions to id 0. A row whose `type` is unknown/mistyped is already dropped
  // upstream by the `union` parser, so only `id` needs enforcing here.
  const enforced = enforce(result.value, result.warnings, ['items[*].id']);
  return {
    items: enforced.items.map(wireToItem),
    total: enforced.total,
    nextCursor: enforced.next_cursor,
  };
};

/**
 * A container locator, mirroring the endpoint's `parent` param: `root` (the
 * zones), `zone:<id>` (a zone's root folders + folder-less objects), or
 * `folder:<id>` (a folder's subfolders + objects).
 */
export type WorkspaceParent = 'root' | `zone:${number}` | `folder:${number}`;

export interface GetWorkspaceContentsParams {
  readonly parent: WorkspaceParent;
  // Restrict the merged result to these types — the tree requests
  // `types: ['folder']` to fetch containers only. Omit for the full contents.
  readonly types?: readonly WorkspaceItemType[];
  // Opaque cursor from a prior page's `nextCursor`; omit for the first page.
  readonly cursor?: string;
  readonly limit?: number;
}

export const getWorkspaceContents = async (params: GetWorkspaceContentsParams): Promise<WorkspaceContents> => {
  const res = await api.get('/workspace/contents', {
    params: {
      parent: params.parent,
      ...(params.types ? { types: params.types.join(',') } : {}),
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.limit ? { limit: params.limit } : {}),
    },
  });
  return parseWorkspaceContents(res.data, 'getWorkspaceContents');
};
